import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { createRootErrorHandlers, ErrorBoundary, ErrorFallback, initRum } from "@recruiter/shared";

import { RUM_SAMPLE, SELLER_API_BASE } from "./config";

// Self-hosted Macan (repo-bundled woff2, no external CDN), Mira's one typeface, as the
// buyer app and admin load it: headings and body separate by WEIGHT, not by a second face.
import "@recruiter/shared/tokens/fonts.css";

// Design tokens load in two tiers, in order (doctrine — see frontend/ARCHITECTURE.md):
// 1. Generated primitives + typography — must load FIRST so the semantic aliases resolve.
import "@recruiter/shared/tokens/variables.css";
// 2. Semantic --rc-color-* aliases (defined against the primitive tier above).
import "@recruiter/shared/tokens.css";
// 3. App-level globals (built on the semantic tier). The talent platform is NEW UI,
//    so — unlike admin — it does NOT copy web's big ported globals.
import "./globals.css";

import { installIapSessionGuard } from "@recruiter/shared/edge";

import { App } from "./App";

// Dev, preprod and previews sit behind Google IAP. The guard keeps a refused API call from
// breaking the page: it sends the page through IAP once and back (@recruiter/shared/edge).
// FIRST, before any code here can fetch. Prod never sets the flag.
if (import.meta.env.VITE_IAP_GATE === "1") installIapSessionGuard();

// Real User Monitoring over keepalive fetch, not a beacon, because local dev is cross-origin
// (SPA on :5375, API on :8200). In the cloud SELLER_API_BASE is empty and the load balancer
// routes this host's /api/* to the API, so the call is same-origin and carries the page's
// cookies, the IAP session among them (@recruiter/shared rum/beacon.ts).
initRum({ app: "seller", endpoint: SELLER_API_BASE, transport: "fetch", sampleRate: RUM_SAMPLE });

const root = document.getElementById("root");
if (!root) throw new Error("missing #root element");

createRoot(root, import.meta.env.PROD ? createRootErrorHandlers("seller") : undefined).render(
  <StrictMode>
    <ErrorBoundary
      fallbackRender={() => (
        <ErrorFallback
          variant="page"
          title="Something went wrong"
          message="Reload the page to try again."
          primaryAction={{ label: "Reload page", onClick: () => window.location.reload() }}
        />
      )}
    >
      <App />
    </ErrorBoundary>
  </StrictMode>,
);
